'use client';

import Navbar from '../../../components/Navbar';
import Footer from '../../../components/Footer';

export default function PrivacyPolicyPage() {
    const lastUpdated = "July 9, 2026";

    const sections = [
        {
            title: "1. Information We Collect",
            content: [
                "We collect personal information that you voluntarily provide to us when registering on the platform, expressing an interest in obtaining information about us or our services, or participating in activities on the platform.",
                "The personal information we collect may include names, email addresses, phone numbers, contact preferences, passwords, and profile details (such as whether you are a buyer, seller, owner, builder, or agent).",
                "We also automatically collect certain information when you visit, use, or navigate the platform. This information does not reveal your specific identity but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, and location."
            ]
        },
        {
            title: "2. How We Use Your Information",
            content: [
                "We process your information for purposes based on legitimate business interests, the fulfillment of our contract with you, compliance with our legal obligations, and/or your consent.",
                "Specifically, we use the information to facilitate account creation and logon process, to send you administrative and marketing communications, to enable user-to-user communications between buyers and sellers, to post testimonials, and to request feedback about your experience.",
                "We also process anonymous, aggregated data for data analysis, identifying usage trends, and determining the effectiveness of our promotional campaigns to improve your browsing experience."
            ]
        },
        {
            title: "3. How We Share Your Information",
            content: [
                "We only share information with your consent, to comply with laws, to provide you with services, to protect your rights, or to fulfill business obligations.",
                "For property inquiries, we share your contact information with the respective property owners, builders, or agents to facilitate the transaction process.",
                "We may share your data with third-party vendors, service providers, contractors, or agents who perform services for us or on our behalf and require access to such information to do that work (such as SMS delivery, email dispatch, or analytics providers)."
            ]
        },
        {
            title: "4. Cookies and Tracking Technologies",
            content: [
                "We may use cookies and similar tracking technologies (like web beacons and pixels) to access or store information.",
                "Cookies help us analyze web traffic, remember your preference settings, and deliver personalized content and advertisements matching your interests.",
                "Most web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your browser to remove cookies and to reject cookies, though this could affect certain features or services of our platform."
            ]
        },
        {
            title: "5. Data Security & Retention",
            content: [
                "We aim to protect your personal information through a system of organizational and technical security measures designed to safeguard any personal info we process.",
                "However, please remember that no transmission over the internet or information storage technology can be guaranteed 100% secure.",
                "We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy policy, unless a longer retention period is required or permitted by law."
            ]
        },
        {
            title: "6. Your Privacy Rights",
            content: [
                "Depending on your location, you may have certain rights under applicable data protection laws. These may include the right to request access and obtain a copy of your personal information, request rectification or erasure, or restrict the processing of your personal information.",
                "You can review, change, or terminate your account at any time by logging into your account settings or contacting us directly using the contact details provided below."
            ]
        },
        {
            title: "7. Updates to This Policy",
            content: [
                "We may update this privacy policy from time to time. The updated version will be indicated by an updated 'Revised' date and the updated version will be effective as soon as it is accessible.",
                "If we make material changes to this privacy policy, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification."
            ]
        },
        {
            title: "8. Contact Us",
            content: [
                "If you have questions or comments about this policy, you may contact our Data Protection Officer by email at privacy@dreamacres.com or by post to:",
                "DreamAcres Tech Private Limited, No 7, 53, Williams Rd, Melapudur, Cantonment, Tiruchirappalli, Tamil Nadu 620001, India."
            ]
        }
    ];

    return (
        <>
            <Navbar />
            <main className="min-h-screen bg-gray-50 pb-16">
                {/* Hero Header */}

                <section className="relative h-24 lg:h-40 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary-dark" />
                    <div className="absolute inset-0 bg-white/5 backdrop-blur-[1px]" />
                    <div className="absolute inset-0 flex items-center">
                        <div className="container-custom">
                            <h1 className="text-3xl lg:text-3xl font-bold text-white mt-4 mb-2">
                                Privacy Policy
                            </h1>
                            <p className="text-white/80 text-sm lg:text-base">
                                Last Updated: {lastUpdated}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Main Content Area */}
                <section className="mt-12">
                    <div className="container-custom">
                        <div className="grid lg:grid-cols-12 gap-8 items-start">
                            {/* Left Navigation Widget (Desktop) */}
                            <div className="lg:col-span-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 sticky top-24 hidden lg:block">
                                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 px-2">
                                    Table of Contents
                                </h3>
                                <nav className="space-y-1">
                                    {sections.map((section, idx) => (
                                        <a
                                            key={idx}
                                            href={`#section-${idx}`}
                                            className="block px-3 py-2 text-sm text-gray-600 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                                        >
                                            {section.title}
                                        </a>
                                    ))}
                                </nav>
                            </div>

                            {/* Policy Content */}
                            <div className="lg:col-span-8 space-y-8">
                                <div className="bg-white p-8 lg:p-10 rounded-2xl shadow-sm border border-gray-100">
                                    <p className="text-gray-600 mb-8 leading-relaxed">
                                        At DreamAcres, we are committed to protecting your personal information and your right to privacy.
                                        This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you
                                        visit our platform or use our services. Please read this privacy policy carefully. If you do not agree
                                        with the terms of this privacy policy, please do not access the site.
                                    </p>

                                    <hr className="border-gray-100 my-8" />

                                    <div className="space-y-10">
                                        {sections.map((section, idx) => (
                                            <div key={idx} id={`section-${idx}`} className="scroll-mt-24">
                                                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-50 flex items-center gap-3">
                                                    <span className="w-1.5 h-6 bg-primary rounded-full"></span>
                                                    {section.title}
                                                </h2>
                                                <div className="space-y-3">
                                                    {section.content.map((p, pIdx) => (
                                                        <p key={pIdx} className="text-gray-600 leading-relaxed text-sm lg:text-base">
                                                            {p}
                                                        </p>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
